// Configuración — the reader's own account as a full-page destination (`/<team>/settings[/<section>]`):
// a section list on the left, the section on the right (one column with a back arrow at phone
// width). Mi perfil + Estado port ios/Sources/Views/OwnProfileView.swift and EditProfileSheet.swift;
// Apariencia and Idioma are this browser's own (localStorage, like the Mac's per-device choices);
// Notificaciones, Pantalla and Seguridad are the Mattermost webapp's account settings, read from
// and written to the server so they follow the person to every device.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import type { AccountPreferences, AccountSnapshot } from '../../../data/features/settings';
import { Icon, type IconName } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { HistoryArrows } from '../../shell/HistoryArrows';
import { useShell } from '../../shell/ShellContext';
import { AccountContext, type AccountState } from './parts';
import { ProfileSection } from './ProfileSection';
import { StatusSection } from './StatusSection';
import { NotificationsSection } from './NotificationsSection';
import { AppearanceSection, LanguageSection } from './LocalSections';
import { DisplaySection } from './DisplaySection';
import { SecuritySection } from './SecuritySection';
import { ReleaseNotesSettingsEntry } from '../pwa/releaseNotes';
import { ChannelTypesSection, useChannelMetaState } from './ChannelTypesSection';
import { BlockedUsersSection } from '../moderation/Moderation';
import './settings.css';

export type SettingsSection = 'profile' | 'status' | 'notifications' | 'appearance' | 'display' | 'language' | 'security' | 'blocked' | 'channelTypes';

export const SECTIONS: Array<{ id: SettingsSection; icon: IconName }> = [
  { id: 'profile', icon: 'person' },
  { id: 'status', icon: 'smile' },
  { id: 'notifications', icon: 'bell' },
  { id: 'appearance', icon: 'sun' },
  { id: 'display', icon: 'eye' },
  { id: 'language', icon: 'globe' },
  { id: 'security', icon: 'lock' },
  // §47: the people the reader blocked.
  { id: 'blocked', icon: 'bellSlash' },
  // §46: system admins only (filtered at render by `can_manage_types`).
  { id: 'channelTypes', icon: 'stack' },
];

export function isSettingsSection(s: string | null | undefined): s is SettingsSection {
  return SECTIONS.some((x) => x.id === s);
}

const isCompact = () => window.matchMedia('(max-width: 760px)').matches;

function useCompact(): boolean {
  const [compact, setCompact] = useState(isCompact);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const on = () => setCompact(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return compact;
}

export function SettingsView({ section, onSignOut }: { section: string | null; onSignOut(): void }) {
  const shell = useShell();
  const compact = useCompact();
  const canManageTypes = useChannelMetaState().canManageTypes;
  const sections = SECTIONS.filter((s) => s.id !== 'channelTypes' || canManageTypes);
  const chosen: SettingsSection | null = isSettingsSection(section) ? section : null;
  // Desktop: no section in the URL shows the first one. Phone: it shows the list.
  const shown: SettingsSection | null = chosen ?? (compact ? null : 'profile');

  const [account, setAccount] = useState<AccountSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<AccountPreferences | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    data.call('accountSnapshot').then(
      (r) => {
        if (!alive) return;
        if ('error' in r) setLoadError(r.error);
        else setAccount(r);
      },
      () => alive && setLoadError('network'),
    );
    data.call('accountPreferences').then(
      (p) => alive && p && setPrefs(p),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const patch = useCallback((change: Partial<AccountSnapshot>) => setAccount((a) => (a ? { ...a, ...change } : a)), []);
  const state: AccountState = useMemo(() => ({ account, loadError, reload, patch, prefs, setPrefs }), [account, loadError, reload, patch, prefs]);

  const go = (s: SettingsSection | null) => shell.openSettings(s);
  const backToList = () => shell.openHome();

  const title = shown ? t(`web.settings.section.${shown}`) : t('web.settings.title');
  const showNav = !compact || !shown;
  const showContent = !!shown;

  return (
    <AccountContext.Provider value={state}>
      <section className="conversation-pane destination st-page" aria-label={t('web.settings.title')}>
        <div className="wallpaper" />
        <header className="conversation-header">
          {compact ? (
            <button
              className="icon-button"
              onClick={() => (shown ? go(null) : backToList())}
              title={shown ? t('web.settings.backToSections') : t('web.nav.backToList')}
              aria-label={shown ? t('web.settings.backToSections') : t('web.nav.backToList')}
            >
              <Icon name="chevronLeft" size={18} strokeWidth={2} />
            </button>
          ) : (
            <HistoryArrows />
          )}
          <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
            <Icon name={compact && shown ? SECTIONS.find((s) => s.id === shown)!.icon : 'gear'} size={18} />
          </span>
          <div className="title-block">
            <h1>{compact ? title : t('web.settings.title')}</h1>
          </div>
        </header>
        <div className="destination-body st-body">
          <div className={`st-layout${showNav && showContent ? ' split' : ''}`}>
            {showNav ? (
              <nav className="st-nav" aria-label={t('web.settings.title')}>
                <div className="group-rows">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`list-row clickable st-nav-row${shown === s.id ? ' active' : ''}`}
                      aria-current={shown === s.id ? 'page' : undefined}
                      onClick={() => go(s.id)}
                    >
                      <span className={`st-nav-icon st-nav-icon-${s.id}`}>
                        <Icon name={s.icon} size={16} strokeWidth={1.9} />
                      </span>
                      <span className="list-row-text">
                        <span className="list-row-title">{t(`web.settings.section.${s.id}`)}</span>
                      </span>
                      {compact ? <Icon name="chevronRight" size={15} /> : null}
                    </button>
                  ))}
                </div>
                <div className="group-rows st-nav-end">
                  <button type="button" className="list-row clickable danger st-nav-row" onClick={onSignOut}>
                    <span className="st-nav-icon st-nav-icon-signout">
                      <Icon name="signOut" size={16} strokeWidth={1.9} />
                    </span>
                    <span className="list-row-text">
                      <span className="list-row-title">{t('profile.signOut')}</span>
                    </span>
                  </button>
                </div>
                <ReleaseNotesSettingsEntry />
                <div className="st-version">{tf('web.settings.version', __APP_VERSION__)}</div>
              </nav>
            ) : null}
            {showContent ? (
              <div className="st-content" key={shown}>
                {!compact ? <h2 className="st-section-title">{title}</h2> : null}
                {shown === 'profile' ? <ProfileSection /> : null}
                {shown === 'status' ? <StatusSection /> : null}
                {shown === 'notifications' ? <NotificationsSection /> : null}
                {shown === 'appearance' ? <AppearanceSection /> : null}
                {shown === 'display' ? <DisplaySection /> : null}
                {shown === 'language' ? <LanguageSection /> : null}
                {shown === 'security' ? <SecuritySection onSignOut={onSignOut} /> : null}
                {shown === 'blocked' ? <BlockedUsersSection /> : null}
                {shown === 'channelTypes' && canManageTypes ? <ChannelTypesSection /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </AccountContext.Provider>
  );
}
